import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIsExtensionInstalled, usePressEscape } from 'hooks';
import { SMART_SCHEDULES } from 'enums/extensionShortIdEnum';
import { SYSTEM } from 'modules/scheduler/enums/groupTypeEnum';
import { GROUP, SCHEDULER_TEMPLATE } from 'enums/groupTypeEnum';
import { INTERNAL, PRIVATE } from 'enums/groupVisibilityEnum';
import { Lock } from '@material-ui/icons';
import { Dialog, Layout, Typography, Radio, TextField, Button, Tooltip } from '@hub-mono/ui';
import styled from 'styled-components';

const StyledLayout = styled(Layout)`
    min-width: 400px;
`;

const SaveAsPopUp = props => {
    const { create, update, config, current, children, ...restOfProps } = props;
    const { saveBtn = {}, saveAsBtn = {}, saveAsGroupBtn = {} } = config;

    const isSmartSchedulesInstalled = useIsExtensionInstalled(SMART_SCHEDULES);
    const { type: currentType, visibility: currentVisibility } = current;
    const initType = currentType || GROUP;
    const initVisibility = currentVisibility || PRIVATE;

    const isSystemGroup = current && SYSTEM === current.groupType;

    const ref = useRef();
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState(restOfProps.name);
    const [type, setType] = useState(initType);
    const [visibility, setVisibility] = useState(initVisibility);
    const toggle = useCallback(event => {
        event && event.preventDefault();
        setIsOpen(prevState => !prevState);
    }, []);

    const isSaveSchedule = type === SCHEDULER_TEMPLATE;
    const radioType = GROUP === type ? type : visibility;

    useEffect(() => {
        if (isOpen) {
            setType(initType);
        }
    }, [initType, isOpen]);

    const handlePressEscape = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    usePressEscape(handlePressEscape);

    useEffect(() => {
        setName(restOfProps.name);
        setType(initType);
        setVisibility(initVisibility);
    }, [initType, initVisibility, restOfProps.name]);

    const onInputChange = event => {
        setName(event.target.value);
    };

    const onTypeChange = event => {
        if ('INTERNAL' === event.target.value || 'PRIVATE' === event.target.value) {
            setVisibility(event.target.value);
            setType(SCHEDULER_TEMPLATE);
            if (props.onTypeChange) {
                props.onTypeChange(SCHEDULER_TEMPLATE);
            }
        } else {
            setType(event.target.value);
            if (props.onTypeChange) {
                props.onTypeChange(event.target.value);
            }
        }
    };

    const onUpdate = () => {
        update({
            title: name,
            groupType: type,
            visibility,
        });
        toggle();
    };

    const onSaveAsNew = () => {
        create({
            title: name,
            groupType: type,
            visibility,
        });
        toggle();
    };

    const getChildrenProps = useMemo(
        () => ({
            toggle,
        }),
        [toggle]
    );

    const InternalLabelJSX = (
        <>
            Internal schedule <Lock style={{ fontSize: '15px', marginTop: '-2px' }} />
        </>
    );

    const PrivateLabelJSX = (
        <>
            Private schedule <Lock style={{ fontSize: '15px', marginTop: '-2px' }} />
        </>
    );

    const titleJSX = (
        <Typography variant="headline" size="small">
            {!saveBtn.hide ? 'Update/Save as new...' : 'Save as new...'}
        </Typography>
    );
    const contentJSX = (
        <StyledLayout stack gap="--Spacing-100">
            <Radio
                checked={radioType === GROUP}
                value={GROUP}
                disabled={saveAsGroupBtn.disabled}
                dataCy={GROUP}
                label="Group"
                onChange={onTypeChange}
            />
            <Radio
                checked={radioType === INTERNAL}
                value={INTERNAL}
                disabled={!isSmartSchedulesInstalled || config.disablePrivateAndInternalRadioOptions}
                dataCy={INTERNAL}
                label={!isSmartSchedulesInstalled ? InternalLabelJSX : 'Internal schedule'}
                onChange={onTypeChange}
            />
            <Radio
                checked={radioType === PRIVATE}
                value={PRIVATE}
                disabled={!isSmartSchedulesInstalled || config.disablePrivateAndInternalRadioOptions}
                dataCy={PRIVATE}
                label={!isSmartSchedulesInstalled ? PrivateLabelJSX : 'Private schedule'}
                onChange={onTypeChange}
            />
            <TextField
                value={name}
                label="Name"
                onChange={onInputChange}
                dataCy="input-field-save-as-value"
                placeholder="Add a name"
            />
        </StyledLayout>
    );
    const actionsJSX = (
        <Layout hAlign="flex-end" gap="--Spacing-100">
            <Button variant="text" dataCy="button--cancel" onClick={toggle}>
                Cancel
            </Button>
            <Tooltip title={saveAsBtn.tooltip}>
                <Button
                    dataCy="button--save-as-new"
                    onClick={onSaveAsNew}
                    disabled={!name || saveAsBtn.disabled || (!isSmartSchedulesInstalled && isSaveSchedule)}
                >
                    Save as new
                </Button>
            </Tooltip>
            {!saveBtn.hide && (
                <Tooltip title={saveBtn.tooltip}>
                    <Button
                        onClick={onUpdate}
                        dataCy="button--update"
                        disabled={
                            !name ||
                            isSystemGroup ||
                            (name === restOfProps.name && visibility === initVisibility && saveBtn.disabled) ||
                            (!isSmartSchedulesInstalled && isSaveSchedule)
                        }
                    >
                        Update
                    </Button>
                </Tooltip>
            )}
        </Layout>
    );

    return (
        <div className="w-100" ref={ref}>
            {children(getChildrenProps)}
            <Dialog
                anchorElement={ref.current}
                headerDivider
                open={isOpen}
                title={titleJSX}
                content={contentJSX}
                actions={actionsJSX}
                onClose={() => {
                    setIsOpen(false);
                }}
            />
        </div>
    );
};

SaveAsPopUp.propTypes = {
    name: PropTypes.string,
    config: PropTypes.shape({
        saveAsBtn: PropTypes.shape({
            disabled: PropTypes.bool,
            tooltip: PropTypes.string,
        }),

        saveBtn: PropTypes.shape({
            disabled: PropTypes.bool,
            tooltip: PropTypes.string,
            hide: PropTypes.bool,
        }),

        saveAsGroupBtn: PropTypes.shape({
            disabled: PropTypes.bool,
        }),

        saveAsScheduleBtn: PropTypes.shape({
            disabled: PropTypes.bool,
        }),

        displayPrivateAndInternalRadioOptions: PropTypes.bool,
        disablePrivateAndInternalRadioOptions: PropTypes.bool,
        directionBottom: PropTypes.bool,
        saveAsButtonLabel: PropTypes.string,
    }),
    current: PropTypes.object,
    onTypeChange: PropTypes.func,
};

SaveAsPopUp.defaultProps = {
    name: '',
    config: {},
};

export default SaveAsPopUp;
